import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal } from "src/components/common";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { ModalSize } from "src/enumerations/ModalSize";
import {
    GroupTrialLaboratoryContractsBreadcrumbs,
    TrialGroupLaboratoryContractsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "src/helpers/date";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialGroupLaboratoryActions from "../../../actions/trialGroupLaboratory";
import * as TrialGroupLaboratoryContractActions from "../../../actions/trialGroupLaboratoryContract";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    GroupTrialLabSubMenu,
    TrialGroupLabSubMenu,
} from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./TrialGroupLaboratoryContractsPage.scss";

interface TrialGroupLaboratoryContractsPageProps {
    trialGroupLaboratoryContracts: Dtos.TrialGroupLaboratoryContract[];
    loadingTrialGroupLaboratoryContracts: boolean;
    loadTrialGroupLaboratoryContractsSuccess: boolean;
    loadTrialGroupLaboratoryContractsFailure: boolean;
    loadTrialGroupLaboratoryContractsErrorCode: string;
    trialGroupLaboratory: Dtos.TrialGroupLaboratory;
    loadingTrialGroupLaboratory: boolean;
    loadTrialGroupLaboratorySuccess: boolean;
    loadTrialGroupLaboratoryFailure: boolean;
    loadTrialGroupLaboratoryErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    deletingTrialGroupLaboratoryContract: boolean;
    deleteTrialGroupLaboratoryContractSuccess: boolean;
    clearTrialGroupLaboratoryContracts: TrialGroupLaboratoryContractActions.ITrialGroupLaboratoryContractClearActionCreator;
    loadTrialGroupLaboratoryContracts: TrialGroupLaboratoryContractActions.ITrialGroupLaboratoryContractLoadByTrialGroupLaboratoryIdActionCreator;
    clearTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryClearActionCreator;
    loadTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    deleteTrialGroupLaboratoryContract: TrialGroupLaboratoryContractActions.ITrialGroupLaboratoryContractDeleteActionCreator;
}

const TrialGroupLaboratoryContractsPage: FC<
    TrialGroupLaboratoryContractsPageProps
> = ({
    trialGroupLaboratoryContracts,
    loadingTrialGroupLaboratoryContracts,
    loadTrialGroupLaboratoryContractsSuccess,
    loadTrialGroupLaboratoryContractsFailure,
    loadTrialGroupLaboratoryContractsErrorCode,
    trialGroupLaboratory,
    loadingTrialGroupLaboratory,
    loadTrialGroupLaboratorySuccess,
    loadTrialGroupLaboratoryFailure,
    loadTrialGroupLaboratoryErrorCode,
    permissions,
    pageSize,
    deletingTrialGroupLaboratoryContract,
    deleteTrialGroupLaboratoryContractSuccess,
    clearTrialGroupLaboratoryContracts,
    loadTrialGroupLaboratoryContracts,
    clearTrialGroupLaboratory,
    loadTrialGroupLaboratory,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize,
    deleteTrialGroupLaboratoryContract
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { groupId } = useParams<{ groupId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const { trialGroupLaboratoryId } = useParams<{
        trialGroupLaboratoryId: string;
    }>();
    const trialIdNumber = parseInt(trialId, 10);
    const groupIdNumber = parseInt(groupId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);
    const trialGroupLaboratoryIdNumber = parseInt(trialGroupLaboratoryId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();

    //
    // -- States --
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [
        tempTrialGroupLaboratoryContract,
        setTempTrialGroupLaboratoryContract,
    ] = useState<Dtos.TrialGroupLaboratoryContract | undefined>(undefined);

    //
    // -- Variables --
    const showTrialGroupLaboratoryContracts =
        !loadingTrialGroupLaboratoryContracts &&
        loadTrialGroupLaboratoryContractsSuccess &&
        !loadingTrialGroupLaboratory &&
        loadTrialGroupLaboratorySuccess;


    // Load Trial group laboratory & contracts
    useEffect(() => {
        if (trialGroupLaboratoryIdNumber && !trialGroupLaboratoryContracts) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryContractsBreadcrumbs
                    : GroupTrialLaboratoryContractsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber
                )
            );

            loadTrialGroupLaboratoryContracts(trialGroupLaboratoryIdNumber);
            loadTrialGroupLaboratory(trialGroupLaboratoryIdNumber);
        }
    }, [
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratoryContracts,
        setBreadcrumbs,
        loadTrialGroupLaboratoryContracts,
        loadTrialGroupLaboratory,
        TrialGroupLaboratoryContractsBreadcrumbs,
        GroupTrialLaboratoryContractsBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupLaboratoryContractsFailure) {
            navigate(`/error/${loadTrialGroupLaboratoryContractsErrorCode}`);
        }
        if (loadTrialGroupLaboratoryFailure) {
            navigate(`/error/${loadTrialGroupLaboratoryErrorCode}`);
        }
    }, [
        loadTrialGroupLaboratoryContractsFailure,
        loadTrialGroupLaboratoryFailure,
        loadTrialGroupLaboratoryContractsErrorCode,
        loadTrialGroupLaboratoryErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialGroupLaboratoryIdNumber &&
            trialGroupLaboratory &&
            trialGroupLaboratory?.trialGroup?.trial &&
            trialGroupLaboratory?.trialGroup?.group
        ) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryContractsBreadcrumbs
                    : GroupTrialLaboratoryContractsBreadcrumbs;

            const drawerItemSetter =
                baseRoute === "trials"
                    ? TrialGroupLabSubMenu
                    : GroupTrialLabSubMenu;

            setDrawerItems(drawerItemSetter(trialGroupLaboratory, permissions));
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratory.trialGroup.trial?.displayName,
                    trialGroupLaboratory.trialGroup.group?.name,
                    trialGroupLaboratory.laboratory?.name
                )
            );
        }
    }, [
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratory,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialGroupLaboratoryContractsBreadcrumbs,
        GroupTrialLaboratoryContractsBreadcrumbs,
        TrialGroupLabSubMenu,
        GroupTrialLabSubMenu,
    ]);

    // Cleanup effect to clear the trial group laboratory & contracts on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroupLaboratory();
            clearTrialGroupLaboratoryContracts();
        };
    }, [clearTrialGroupLaboratory, clearTrialGroupLaboratoryContracts]);

    // Reload trial group laboratory & contracts after delete
    useEffect(() => {
        if (
            !deletingTrialGroupLaboratoryContract &&
            deleteTrialGroupLaboratoryContractSuccess
        ) {
            loadTrialGroupLaboratory(trialGroupLaboratoryIdNumber);
            loadTrialGroupLaboratoryContracts(trialGroupLaboratoryIdNumber);
        }
    }, [
        deletingTrialGroupLaboratoryContract,
        deleteTrialGroupLaboratoryContractSuccess,
        loadTrialGroupLaboratory,
        loadTrialGroupLaboratoryContracts,
        trialGroupLaboratoryIdNumber,
    ]);

    const generateDeleteButton = (
        trialGroupLaboratoryContract: Dtos.TrialGroupLaboratoryContract
    ) => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialGroupLaboratoryContractUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`contractDeleteButton-${trialGroupLaboratoryContract.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setTempTrialGroupLaboratoryContract(
                        trialGroupLaboratoryContract
                    );
                    setOpenModal(true);
                }}
            >
                <FontAwesomeIcon
                    icon={FontAwesomeIcons.Duotone.TRASH}
                    fixedWidth
                />
            </button>
        );
    };

    const createTable = useMemo(() => {
        let tableData = trialGroupLaboratoryContracts;

        if (!tableData || tableData?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no trial group laboratory
                            contracts to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = tableData.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={tableData}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : tableData.length
                    }
                    pageSize={showPagination ? pageSize : tableData.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no trial group laboratory contracts to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const baseRoute =
                                    location?.pathname?.split("/")[1];
                                const url =
                                    baseRoute === "trials"
                                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/contracts/${rowInfo.original.id}/details`
                                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/contracts/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.INSTITUTION}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                        },
                        {
                            id: "dateSentToSite",
                            Header: "Date Sent To Site",
                            accessor: "dateSentToSite",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "dateConfirmedBySite",
                            Header: "Date Sent To Site",
                            accessor: "dateConfirmedBySite",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "deleteContract",
                            Header: " ",
                            maxWidth: 75,
                            Cell: (props) => (
                                <div>
                                    {generateDeleteButton(props.original)}
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        trialGroupLaboratory,
        trialGroupLaboratoryContracts,
        pageSize,
        PAGE_SIZE_OPTIONS,
        navigate,
        setPageSize,
        trialIdNumber,
        trialGroupLaboratoryIdNumber,
    ]);

    const generateCreateLink = () => {
        const baseRoute = location?.pathname?.split("/")[1];
        const url =
            baseRoute === "trials"
                ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/contracts/create`
                : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/contracts/create`;
        return url;
    };

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-6 mb-2"}>
                        <h1>Contracts</h1>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialGroupLaboratoryContractCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={generateCreateLink()}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialGroupLaboratoryContracts ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialGroupLaboratoryContracts ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showTrialGroupLaboratoryContracts,
        createTable,
        trialIdNumber,
        trialGroupLaboratoryIdNumber,
        generateCreateLink,
    ]);

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialGroupLaboratoryContractUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            return (
                <Modal
                    open={openModal}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setOpenModal(false);
                        setTempTrialGroupLaboratoryContract(undefined);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h2 className="mb-0">Delete</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setOpenModal(false);
                                setTempTrialGroupLaboratoryContract(undefined);
                            }}
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={FontAwesomeIcons.Regular.TIMES}
                            />
                        </button>
                    </div>
                    <div className="modal-body">
                        <span>
                            Are you sure you want to delete the contract "
                            <em>{tempTrialGroupLaboratoryContract?.name}"</em>{" "}
                            from trial group laboratory{" "}
                            <strong>
                                {
                                    trialGroupLaboratory?.trialGroup?.trial
                                        ?.displayName
                                }{" "}
                                -{" "}
                                {trialGroupLaboratory?.trialGroup?.group?.name}
                            </strong>
                            .
                        </span>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                deleteTrialGroupLaboratoryContract(
                                    tempTrialGroupLaboratoryContract?.id
                                );
                                setOpenModal(false);
                                setTempTrialGroupLaboratoryContract(undefined);
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setOpenModal(false);
                                setTempTrialGroupLaboratoryContract(undefined);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        trialGroupLaboratory,
        tempTrialGroupLaboratoryContract,
        openModal,
        permissions,
        setOpenModal,
        setTempTrialGroupLaboratoryContract,
        trialGroupLaboratoryIdNumber,
        loadTrialGroupLaboratoryContracts,
        deleteTrialGroupLaboratoryContract,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLaboratoryContractView}
            loading={
                loadingTrialGroupLaboratoryContracts ||
                loadingTrialGroupLaboratory
            }
        >
            {renderContent}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupLaboratoryContractsPageProps> => {
    let trialGroupLaboratoryContracts:
        | Dtos.TrialGroupLaboratoryContract[]
        | undefined =
        state.trialGroupLaboratoryContract.formData instanceof Array
            ? state.trialGroupLaboratoryContract.formData
            : undefined;
    let trialGroupLaboratory: Dtos.TrialGroupLaboratory | undefined = !(
        state.trialGroupLaboratory.formData instanceof Array
    )
        ? state.trialGroupLaboratory.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroupLaboratoryContracts: trialGroupLaboratoryContracts!,
        loadingTrialGroupLaboratoryContracts:
            state.trialGroupLaboratoryContract.loadState &&
            state.trialGroupLaboratoryContract.loadState.status ==
                RequestState.Pending,
        loadTrialGroupLaboratoryContractsSuccess:
            state.trialGroupLaboratoryContract.loadState &&
            state.trialGroupLaboratoryContract.loadState.status ==
                RequestState.Success,
        loadTrialGroupLaboratoryContractsFailure:
            state.trialGroupLaboratoryContract.loadState &&
            state.trialGroupLaboratoryContract.loadState.status ==
                RequestState.Failure,
        loadTrialGroupLaboratoryContractsErrorCode:
            state.trialGroupLaboratoryContract.loadState.errorCode,
        trialGroupLaboratory: trialGroupLaboratory!,
        loadingTrialGroupLaboratory:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Pending,
        loadTrialGroupLaboratorySuccess:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Success,
        loadTrialGroupLaboratoryFailure:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Failure,
        loadTrialGroupLaboratoryErrorCode:
            state.trialGroupLaboratory.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        deletingTrialGroupLaboratoryContract:
            state.trialGroupLaboratoryContract.deleteState &&
            state.trialGroupLaboratoryContract.deleteState.status ==
                RequestState.Pending,
        deleteTrialGroupLaboratoryContractSuccess:
            state.trialGroupLaboratoryContract.deleteState &&
            state.trialGroupLaboratoryContract.deleteState.status ==
                RequestState.Success,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupLaboratoryContractsPageProps> => {
    return {
        loadTrialGroupLaboratoryContracts: bindActionCreators(
            TrialGroupLaboratoryContractActions.LoadTrialGroupLaboratoryContractByTrialGroupLaboratoryId,
            dispatch
        ),
        clearTrialGroupLaboratoryContracts: bindActionCreators(
            TrialGroupLaboratoryContractActions.Clear,
            dispatch
        ),
        loadTrialGroupLaboratory: bindActionCreators(
            TrialGroupLaboratoryActions.LoadTrialGroupLaboratoryById,
            dispatch
        ),
        clearTrialGroupLaboratory: bindActionCreators(
            TrialGroupLaboratoryActions.Clear,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        deleteTrialGroupLaboratoryContract: bindActionCreators(
            TrialGroupLaboratoryContractActions.DeleteTrialGroupLaboratoryContract,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupLaboratoryContractsPage);
